import { Divider } from '@dynatrace/strato-components/layouts';
import Spacings from '@dynatrace/strato-design-tokens/spacings';
import { ZoomToSelectionIcon } from '@dynatrace/strato-icons';

import { disabledActions } from './HistogramSelectionAreaTooltip.sty.js';
import { getCustomContextMenuItems } from '../../../../core/components/interactions-overlay/utils/get-custom-context-menu-items.js';
import {
  CustomActions,
  CustomIntents,
} from '../../../../core/components/selection-area/tooltip/CustomActions.js';
import { ChartTooltip } from '../../../../core/components/tooltip/ChartTooltip/ChartTooltip.js';
import { useSelectState } from '../../../hooks/store/selectors.js';
import { useHistogramFilteredData } from '../../../hooks/useHistogramFilteredData.js';
import { useHistogramInteractions } from '../../../hooks/useHistogramInteractions.js';
import { useHistogramSelectConfig } from '../../../hooks/useHistogramSelectConfig.js';
import { useHistogramXFormatter } from '../../../hooks/useHistogramXFormatter.js';
import { useHistogramXScale } from '../../../hooks/useHistogramXScale.js';
import { useZoomToSelection } from '../../../hooks/useZoomToSelection.js';
import {
  type HistogramBin,
  HistogramSeries,
} from '../../../types/histogram.js';
import { isBinWithinRange } from '../utils/is-bin-within-range.js';

export const HistogramSelectionAreaTooltip = () => {
  const selectState = useSelectState();
  const zoomToSelection = useZoomToSelection();
  const { zoomX } = useHistogramInteractions();
  const filteredData = useHistogramFilteredData();
  const { xScale } = useHistogramXScale();
  const formatter = useHistogramXFormatter();
  const selectConfig = useHistogramSelectConfig();

  if (!selectState) {
    return null;
  }

  const [from, to] = [
    xScale.invert(selectState.start.relative.x),
    xScale.invert(selectState.end.relative.x),
  ];

  const selectedSeries: HistogramSeries[] = [];
  let totalBins = 0;

  for (const series of filteredData.series) {
    let isSeriesPresent = false;
    const seriesBins: HistogramBin[] = [];

    for (const bin of series.bins) {
      if (isBinWithinRange(bin, from, to)) {
        totalBins++;
        seriesBins.push(bin);
        isSeriesPresent = true;
      }
    }

    if (isSeriesPresent) {
      selectedSeries.push({ ...series, bins: seriesBins });
    }
  }

  const actions = selectConfig?.actions;

  const customContextMenu = actions
    ? getCustomContextMenuItems(actions(selectedSeries, [from, to]))
    : null;

  const zoomEnabled = zoomX?.enabled;
  const hasActions = !!customContextMenu?.actions.length || zoomEnabled;

  const hasIntents = !!customContextMenu?.intents.length;

  const hasMenu = zoomEnabled || hasActions || hasIntents;

  return (
    <ChartTooltip
      position={selectState.end.absolute}
      style={{
        overflowY: 'auto',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    >
      <ChartTooltip.RangeHeader from={from} to={to} formatter={formatter} />
      <ChartTooltip.Item showDivider={hasMenu}>
        <ChartTooltip.TextHeader
          text={`${totalBins} bins across ${selectedSeries.length} series`}
        />
      </ChartTooltip.Item>
      <div className={selectState.finished ? undefined : disabledActions}>
        {zoomX?.enabled ? (
          <ChartTooltip.ActionItem
            text={'Zoom to selection'}
            icon={<ZoomToSelectionIcon />}
            onMouseUp={(event) => {
              event.stopPropagation();
              zoomToSelection();
            }}
            shortcut={'⏎'}
          />
        ) : null}

        <CustomActions actions={customContextMenu?.actions} />

        {hasIntents && hasActions && (
          <Divider
            orientation="horizontal"
            style={{ margin: `${Spacings.Size4} 0` }}
          />
        )}

        <CustomIntents intents={customContextMenu?.intents} />
      </div>
    </ChartTooltip>
  );
};
